import { TransactionSchedulePlan } from '../types';
import { dateDiff, dt } from '../util/dateTime';
import { calculateForecastAmount } from './calculateForecastAmount';

type DateValue = { date: string; balance: number };

export type ForecastDatePlan = {
    startAmount: number;
    annualGrowthRate: number;
    transactionSchedulePlans?: TransactionSchedulePlan[];
};

/**
 * Given a start date, target amount, and optional plans (combo start amount
 * + recurring deposits + annual return), determine date when targetAmount will
 * be reached (if at all).
 * We've honed this in on O(log(n)) time, though an ideal solution would be
 * closer to O(n).
 * TODO: Explore more elegant approaches to this algo. */
export const calculateForecastDate = ({
    startDate,
    targetDate,
    targetAmount,
    plans,
}: {
    startDate: string;
    targetAmount: number;
    /** Pass targetDate to potentially speed up algorithm */
    targetDate?: string | null;
    plans: ForecastDatePlan[];
}): string | undefined => {
    // Used to "check" balance on a given date. Workhorse for the narrowing
    // logic below.
    const balanceOnDate = (date: string) => {
        let balance = 0;

        for (const plan of plans) {
            const fvStartAmount = calculateForecastAmount({
                startAmount: plan.startAmount,
                annualGrowthRate: plan.annualGrowthRate,
                startDate,
                targetDate: date,
                transactionSchedulePlans: plan.transactionSchedulePlans,
            });

            balance += fvStartAmount;
        }

        return balance;
    };

    // Establish high & low bounds for narrowing algo.
    targetDate = targetDate ?? dt(startDate).add(5, 'y').toDateStr();
    const maxDate = dt(startDate).add(100, 'y').toDateStr();

    let low = {
        date: startDate,
        balance: balanceOnDate(startDate),
    };
    let high = {
        date: targetDate,
        balance: balanceOnDate(targetDate),
    };

    // Start with high == target date; if undershooting, increase exponentially
    // until high amount > target amount or max date is reached.

    while (high.balance < targetAmount) {
        if (high.date >= maxDate) return;
        const diff = Math.max(dateDiff(high.date, low.date, 'y'), 1);
        let highDate = dt(high.date).add(diff, 'y').toDateStr();
        highDate = highDate < maxDate ? highDate : maxDate;
        high = { date: highDate, balance: balanceOnDate(highDate) };
    }

    // Function with algo to narrow in on first date where balance > target amount.
    // Should be analagous to binary search on a sorted list (ie "O log(n)").

    const narrowRange = (
        low: DateValue,
        high: DateValue
    ): { low: DateValue; high: DateValue } => {
        const diff = dateDiff(high.date, low.date, 'd');

        // If num ticks between high & low is 1 or 0, we've narrowed as far as
        // we can go
        if (diff < 2) return { low, high };

        const midDate = dt(low.date)
            .add(Math.floor(diff / 2), 'd')
            .toDateStr();
        const midBalance = balanceOnDate(midDate);
        const mid = { date: midDate, balance: midBalance };

        return mid.balance <= targetAmount
            ? { low: mid, high }
            : { low, high: mid };
    };

    // Simple sanity threshold.
    // Max options should be 365 * 100 = 36500; log2(36500) = 15
    const maxOps = 25;
    for (let i = 0; i < maxOps; i++) {
        if (low.balance >= targetAmount) return low.date;
        if (dateDiff(high.date, low.date, 'd') < 2) {
            return low.balance < targetAmount ? high.date : low.date;
        }
        const narrowed = narrowRange(low, high);
        low = narrowed.low;
        high = narrowed.high;
    }
};
